import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import GeoArt from "../components/GeoArt";
import { MaskedLines, Reveal, SectionHead } from "../components/Reveal";

const PROGRAMS = [
  { no: "01", title: "Artist Spotlights", body: "Dedicated features and exhibitions that give emerging artists a serious stage and a serious audience." },
  { no: "02", title: "Artist Markets", body: "Curated markets connecting artists directly with collectors and community — no gatekeepers." },
  { no: "03", title: "Youth Arts Programming", body: "Workshops and mentorship putting materials, language, and confidence in young hands early." },
  { no: "04", title: "Community Exhibitions", body: "Shows built with and for the neighborhood — art that belongs to the people it pictures." },
  { no: "05", title: "Future Cultural Initiatives", body: "An evolving roadmap of programs, partnerships, and spaces dedicated to cultural development." },
];

export default function Collection() {
  return (
    <main className="pt-24 sm:pt-32" data-testid="collection-page">
      <section className="container-x section-y">
        <p className="eyebrow mb-6" data-testid="collection-eyebrow">A Cultural Arts Initiative</p>
        <MaskedLines lines={["The", "Collection"]} className="h-display !text-6xl sm:!text-8xl lg:!text-9xl" />
        <Reveal delay={0.35}>
          <p className="mt-10 max-w-2xl text-muted-foreground" data-testid="collection-mission">
            Building community through art, education, and creative opportunities. The Collection is a community and cultural development initiative — the connective tissue between artists, audiences, and the neighborhoods that raise them.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionHead eyebrow="Programs" title="What The Collection Builds" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {PROGRAMS.map((p, i) => (
              <Reveal key={p.no} delay={i * 0.06} className="bg-card">
                <div className="p-8 sm:p-10 h-full hover:bg-secondary/50 transition-colors duration-500" data-testid={`program-${p.no}`}>
                  <span className="font-mono-x text-xs text-[#FF1A1A]">{p.no}</span>
                  <h3 className="font-display font-extrabold uppercase text-xl mt-3 mb-3">{p.title}</h3>
                  <p className="text-sm text-muted-foreground">{p.body}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="bg-[#FF1A1A]">
              <Link to="/contact?category=General Contact" className="p-8 sm:p-10 h-full flex flex-col justify-between text-white min-h-[220px]" data-testid="collection-join-cta">
                <span className="font-mono-x text-xs opacity-80">GET INVOLVED</span>
                <span className="font-display font-black uppercase text-2xl flex items-center gap-3">Build With Us <ArrowRight size={22} /></span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="border border-border p-4 -rotate-1 hover:rotate-0 transition-transform duration-700">
              <GeoArt seed="collection-community" className="w-full" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <p className="eyebrow mb-4">Why It Matters</p>
              <h2 className="h-section mb-6">Culture Is Infrastructure</h2>
              <p className="text-muted-foreground max-w-md mb-6">
                Scenes don't sustain themselves. They need rooms to show in, markets to sell in, and mentors to call. The Collection builds that infrastructure — one exhibition, one market, one young artist at a time.
              </p>
              <Link to="/news" className="btn-ghost" data-testid="collection-news-cta">See What's Happening <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
